import MediaQuery from "cms/components/MediaQuery";
import OptimizedImage from "cms/components/OptimizedImage";
import ValuePropositionCard from "cms/components/ValuePropositionCard";
import ValuePropositionHeader from "cms/components/ValuePropositionHeader";
import { graphql } from "gatsby";
import React, { useCallback, useState } from "react";
import { useSwipeable } from "react-swipeable";

import {
  CarouselIndicator,
  CarouselIndicatorContainer,
  Container,
  Content,
  DesktopContent,
  Illustration,
  Slider,
  SliderWrapper,
  ValuePropositionCardContainer,
} from "./MordorValuePropositionBlock.styles";

export interface MordorValuePropositionBlockProps
  extends Queries.SanityMordorValuePropositionBlock {}

const MordorValuePropositionBlock: React.FC<MordorValuePropositionBlockProps> =
  ({ headline, _rawDescription, button, valuePropositionCards }) => {
    const [clickedIndex, setClickedIndex] = useState(0);
    const [activeIndex, setActiveIndex] = useState(0);
    const [position, setPosition] = useState(0);

    const OnClick = useCallback((index: number) => {
      setClickedIndex(index);
    }, []);

    const OnSwipeLeft = useCallback(() => {
      if (valuePropositionCards === null) return;

      setPosition((prevPosition) =>
        Math.min(prevPosition + 1, valuePropositionCards.length - 1),
      );
      if (activeIndex < valuePropositionCards.length - 1) {
        setActiveIndex(activeIndex + 1);
      }
    }, [activeIndex, valuePropositionCards]);

    const OnSwipeRight = useCallback(() => {
      setPosition((prevPosition) => Math.max(prevPosition - 1, 0));
      if (activeIndex > 0) {
        setActiveIndex(activeIndex - 1);
      }
    }, [activeIndex]);

    const handlers = useSwipeable({
      onSwipedLeft: OnSwipeLeft,
      onSwipedRight: OnSwipeRight,
    });

    return (
      <Container {...handlers}>
        <Content>
          <ValuePropositionHeader
            headline={headline}
            description={_rawDescription}
            button={button}
          />

          <MediaQuery $lessThan="large" style={{ width: "100%" }}>
            <SliderWrapper>
              <Slider $position={position}>
                {valuePropositionCards?.map((card, index) => (
                  <ValuePropositionCard
                    key={index}
                    card={card}
                    $isActive={index === activeIndex}
                  />
                ))}
              </Slider>
            </SliderWrapper>

            <CarouselIndicatorContainer>
              {valuePropositionCards?.map((_, index) => (
                <CarouselIndicator
                  key={index}
                  $isActive={index === activeIndex}
                />
              ))}
            </CarouselIndicatorContainer>
          </MediaQuery>

          <MediaQuery $greaterThan="large" style={{ width: "100%" }}>
            <DesktopContent>
              <ValuePropositionCardContainer>
                {valuePropositionCards?.map((card, index) => (
                  <ValuePropositionCard
                    key={index}
                    card={card}
                    $isActive={index === clickedIndex}
                    onClick={() => OnClick(index)}
                  />
                ))}
              </ValuePropositionCardContainer>

              {valuePropositionCards?.map((card, index) => (
                <Illustration key={index} $show={index === clickedIndex}>
                  {card?.imageDesktop && (
                    <OptimizedImage
                      image={card?.imageDesktop}
                      alt={card?.imagesAlt}
                    />
                  )}
                </Illustration>
              ))}
            </DesktopContent>
          </MediaQuery>
        </Content>
      </Container>
    );
  };

export const query = graphql`
  fragment MordorValuePropositionBlock on SanityMordorValuePropositionBlock {
    _key
    _type
    headline
    _rawDescription(resolveReferences: { maxDepth: 10 })
    button {
      variant
      label
      url
    }
    valuePropositionCards {
      icon { ...OptimizedImage }
      headline
      giveTextMagicColor
      _rawCardDescription(resolveReferences: { maxDepth: 10 })
      imageMobile { ...OptimizedImage }
      imageTablet { ...OptimizedImage }
      imageDesktop { ...OptimizedImage }
      imagesAlt
    }
  }
`;

export default MordorValuePropositionBlock;
